import { useMemo, useState } from "react"
import { Badge, Button, Col, Row } from "reactstrap"
import Select from "react-select"

import PageWrapper from "components/Common/PageWrapper"
import TableContainer from "components/Common/TableContainer"
import { formatUiDate } from "utils/formatDate"
import { useListParams } from "hooks/useListParams"
import { useComplaintMeta, useComplaints } from "hooks/complaintHook"
import ComplaintDetailModal from "./ComplaintDetailModal"

const STATUS_COLORS = {
  open: "warning",
  in_review: "info",
  resolved: "success",
  closed: "secondary",
}

const titleCase = s =>
  String(s || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, c => c.toUpperCase())

const ComplaintList = () => {
  document.title = "Complaint Box"

  const { params, setPage } = useListParams({ page: 1 })
  const [status, setStatus] = useState(null)
  const [category, setCategory] = useState(null)
  const [selected, setSelected] = useState(null)

  const { data: metaResp } = useComplaintMeta()
  const meta = metaResp?.data ?? {}

  const queryParams = useMemo(
    () => ({
      page: params.page ?? 1,
      ...(status ? { status } : {}),
      ...(category ? { category } : {}),
    }),
    [params.page, status, category]
  )

  const { data: resp, isLoading, isFetching, refetch } = useComplaints(queryParams)
  const rows = resp?.data?.data ?? []
  const paginationInfo = resp?.data

  const columns = useMemo(
    () => [
      {
        header: "Subject",
        cell: ({ row }) => (
          <div>
            <div className="fw-medium">{row.original.subject}</div>
            <small className="text-muted text-capitalize">
              {titleCase(row.original.category)}
            </small>
          </div>
        ),
      },
      {
        header: "From",
        cell: ({ row }) =>
          row.original.is_anonymous
            ? <Badge color="light" className="text-dark">Anonymous</Badge>
            : row.original.employee?.name ?? "—",
      },
      {
        header: "Showroom",
        cell: ({ row }) => row.original.showroom?.name ?? "—",
      },
      {
        header: "Status",
        cell: ({ row }) => (
          <Badge color={STATUS_COLORS[row.original.status] ?? "light"}>
            {titleCase(row.original.status)}
          </Badge>
        ),
      },
      {
        header: "Submitted",
        cell: ({ row }) => formatUiDate(row.original.created_at),
      },
      {
        header: "Action",
        cell: ({ row }) => (
          <Button size="sm" color="primary" onClick={() => setSelected(row.original.id)}>
            <i className="mdi mdi-eye-outline" /> View
          </Button>
        ),
      },
    ],
    []
  )

  const statusOptions = (meta.statuses ?? []).map(s => ({ value: s, label: titleCase(s) }))
  const categoryOptions = (meta.categories ?? []).map(c => ({ value: c, label: titleCase(c) }))

  return (
    <PageWrapper title="Complaint Box" breadcrumbItem="Complaints">
      <Row className="mb-3 g-2">
        <Col md={3}>
          <Select
            options={statusOptions}
            value={statusOptions.find(o => o.value === status) ?? null}
            onChange={o => setStatus(o?.value ?? null)}
            placeholder="All statuses"
            isClearable
          />
        </Col>
        <Col md={3}>
          <Select
            options={categoryOptions}
            value={categoryOptions.find(o => o.value === category) ?? null}
            onChange={o => setCategory(o?.value ?? null)}
            placeholder="All categories"
            isClearable
          />
        </Col>
        <Col className="text-end">
          <Button color="light" onClick={() => refetch()} disabled={isFetching}>
            <i className={`mdi mdi-refresh ${isFetching ? "mdi-spin" : ""}`} /> Refresh
          </Button>
        </Col>
      </Row>

      <TableContainer
        columns={columns}
        data={rows}
        isPagination
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isFetching}
        className="table-responsive"
      />

      {selected && (
        <ComplaintDetailModal
          id={selected}
          isOpen={Boolean(selected)}
          toggle={() => setSelected(null)}
        />
      )}
    </PageWrapper>
  )
}

export default ComplaintList
